import React, { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { bwColours } from "./theme";

const colours = {
  key: bwColours.accent.blue,
  string: bwColours.accent.green,
  number: bwColours.accent.brown,
  boolean: bwColours.accent.purple,
  null: "#8A8A8A",
};

// Fixed-width box with a matching negative margin: the key lands at the
// row's start position (aligned with siblings that have no triangle), and
// the glyph sits in the indentation space to its left. line-height 1 keeps
// the larger glyph from stretching the row's line height.
const Triangle = ({ open }: { open: boolean }) => (
  <span
    aria-hidden
    style={{
      display: "inline-block",
      width: 18,
      marginLeft: -18,
      fontSize: 18,
      lineHeight: 1,
      verticalAlign: "middle",
      textAlign: "center",
      userSelect: "none",
    }}
  >
    {open ? "▾" : "▸"}
  </span>
);

const JsonValue = ({ value }: { value: any }) => {
  if (typeof value === "string") {
    return (
      <span style={{ color: colours.string }}>{JSON.stringify(value)}</span>
    );
  }
  if (typeof value === "number") {
    return <span style={{ color: colours.number }}>{String(value)}</span>;
  }
  if (typeof value === "boolean") {
    return <span style={{ color: colours.boolean }}>{String(value)}</span>;
  }
  return <span style={{ color: colours.null }}>null</span>;
};

const JsonNode = ({
  name,
  value,
  depth,
  defaultDepth,
  last,
}: {
  name?: string | number;
  value: any;
  depth: number;
  defaultDepth: number;
  last: boolean;
}) => {
  const [expanded, setExpanded] = useState(depth < defaultDepth);
  const label =
    name !== undefined ? (
      <span style={{ color: colours.key }}>{JSON.stringify(name)}: </span>
    ) : null;

  if (value === null || typeof value !== "object") {
    return (
      <Box component="div">
        {label}
        <JsonValue value={value} />
        {!last && ","}
      </Box>
    );
  }

  const isArray = Array.isArray(value);
  const entries: [string | number, any][] = isArray
    ? value.map((v, i) => [i, v])
    : Object.entries(value);
  const [open, close] = isArray ? ["[", "]"] : ["{", "}"];

  if (entries.length === 0) {
    return (
      <Box component="div">
        {label}
        <span>
          {open}
          {close}
        </span>
        {!last && ","}
      </Box>
    );
  }

  if (!expanded) {
    return (
      <Box
        component="div"
        role="button"
        aria-expanded={false}
        onClick={() => setExpanded(true)}
        sx={{ cursor: "pointer" }}
      >
        <Triangle open={false} />{label}
        <span>
          {open}…{close}{" "}
          <span style={{ color: colours.null }}>({entries.length})</span>
        </span>
        {!last && ","}
      </Box>
    );
  }

  // 2.4em = 4 monospace spaces of indentation (0.6em advance per space)
  return (
    <Box component="div">
      <Box
        component="div"
        role="button"
        aria-expanded={true}
        onClick={() => setExpanded(false)}
        sx={{ cursor: "pointer" }}
      >
        <Triangle open={true} />{label}
        <span>{open}</span>
      </Box>
      <Box component="div" sx={{ ml: "2.4em" }}>
        {entries.map(([k, v], i) => (
          <JsonNode
            key={k}
            name={isArray ? undefined : k}
            value={v}
            depth={depth + 1}
            defaultDepth={defaultDepth}
            last={i === entries.length - 1}
          />
        ))}
      </Box>
      <Box component="div">
        {close}
        {!last && ","}
      </Box>
    </Box>
  );
};

// Renders a JSON value as a collapsible, syntax-coloured tree. Objects and
// arrays are expanded by default down to `defaultDepth` levels; click any of
// them to toggle. The buttons re-expand the whole tree from scratch.
const JsonView = ({ data }: { data: any }) => {
  const [tree, setTree] = useState({ id: 0, defaultDepth: 2 });
  if (data === undefined) {
    return null;
  }
  // the tree container's pl makes room for the root triangle, which sits
  // left of the key
  return (
    <Box sx={{ fontFamily: "monospace", fontSize: 13 }}>
      <Box sx={{ mb: 1 }}>
        <Button
          size="small"
          onClick={() =>
            setTree((t) => ({ id: t.id + 1, defaultDepth: Infinity }))
          }
        >
          Expand all
        </Button>
        <Button
          size="small"
          onClick={() => setTree((t) => ({ id: t.id + 1, defaultDepth: 0 }))}
        >
          Collapse all
        </Button>
      </Box>
      <Box
        component="div"
        key={tree.id}
        sx={{ overflow: "auto", pl: "18px" }}
      >
        <JsonNode value={data} depth={0} defaultDepth={tree.defaultDepth} last />
      </Box>
    </Box>
  );
};

export { JsonView };
